<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import AutoComplete from 'primevue/autocomplete'
import Checkbox from 'primevue/checkbox'
import { useAppToast } from '@/shared/lib/useAppToast'

import { getAttributesByType } from '../../attributes/api/attributes.service'
import { createValue } from '../../attributes/api/attributeValues.service'
import { uploadImage, getGalleryImages } from '@/shared/api/files.service'

const props = defineProps({
  visible: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  product: { type: Object, default: null },
  productTypes: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'submit'])

const toast = useAppToast()

const articul = ref('')
const basePrice = ref(0)
const salePrice = ref(null)
const saleIsActive = ref(false)
const quantity = ref(0)
const productTypeSelected = ref(null)
const filteredProductTypes = ref([])
const productTypeLastQuery = ref('')
const productTypeId = computed(() =>
  productTypeSelected.value && typeof productTypeSelected.value === 'object'
    ? productTypeSelected.value.value
    : null
)
const images = ref([{ id: null, url: '', isDefault: true, uploading: false }])

const attributes = ref([])
const loadingAttributes = ref(false)

const fileInput = ref(null)
const uploadingCount = ref(0)
const galleryVisible = ref(false)
const galleryImages = ref([])
const pendingImageIndex = ref(null)

const localErrors = ref({
  articul: '',
  productTypeId: '',
  images: ''
})

const productTypeOptions = computed(() =>
  props.productTypes.map((pt) => ({ label: pt.name, value: pt.id }))
)

const dialogTitle = computed(() =>
  props.mode === 'edit' ? 'Редактирование товара' : 'Создание товара'
)

const submitLabel = computed(() =>
  props.mode === 'edit' ? 'Сохранить' : 'Создать'
)

watch(
  () => props.visible,
  (val) => {
    if (val) {
      fillForm()
    } else {
      resetForm()
    }
  }
)

watch(
  () => props.product,
  () => {
    if (props.visible) fillForm()
  }
)

watch(productTypeId, async (newId) => {
  if (!newId) {
    attributes.value = []
    return
  }

  await loadAttributesForType(newId)
})

async function loadAttributesForType(typeId) {
  try {
    loadingAttributes.value = true

    const allAttrs = await getAttributesByType(typeId)

    const attrMap = new Map()
    allAttrs.forEach((attr) => {
      const name = attr.attributeName ?? attr.name ?? 'Без названия'
      const attrId =
        attr.attributeId ??
        attr.productAttributeId ??
        attr.characteristicId ??
        name
      const mapKey = `${attrId}:${name}`

      if (!attrMap.has(mapKey)) {
        attrMap.set(mapKey, {
          id: attrId,
          name,
          values: [],
          selectedValue: null,
          filteredValues: [],
          lastQuery: '',
          error: ''
        })
      }

      attrMap.get(mapKey).values.push({
        id: attr.valueId ?? attr.id,
        value: attr.value
      })
    })

    attributes.value = Array.from(attrMap.values())

    applySelectedAttributeValues()
  } catch (error) {
    attributes.value = []
    toast.error(error.message || 'Ошибка при загрузке атрибутов типа товара')
  } finally {
    loadingAttributes.value = false
  }
}

function toNumberOrNull(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function getProductSelectedValues() {
  const selected = []

  const collections = [
    props.product?.characteristicValues,
    props.product?.CharacteristicValues,
    props.product?.variants,
    props.product?.Variants
  ]

  collections.forEach((collection) => {
    if (!Array.isArray(collection)) return

    collection.forEach((item) => {
      const valueId = toNumberOrNull(
        item.valueId ??
          item.ValueId ??
          item.characteristicValueId ??
          item.CharacteristicValueId
      )

      if (!valueId) return

      selected.push({
        attributeId: toNumberOrNull(
          item.attributeId ??
            item.AttributeId ??
            item.characteristicId ??
            item.CharacteristicId
        ),
        valueId
      })
    })
  })

  if (Array.isArray(props.product?.attributes)) {
    props.product.attributes.forEach((attr) => {
      const valueId = toNumberOrNull(attr.selectedValueId ?? attr.SelectedValueId)
      if (!valueId) return

      selected.push({
        attributeId: toNumberOrNull(attr.id ?? attr.Id),
        valueId
      })
    })
  }

  return selected
}

function applySelectedAttributeValues() {
  const selectedValues = getProductSelectedValues()
  const selectedValueIds = new Set(selectedValues.map((item) => item.valueId))

  attributes.value.forEach((attr) => {
    const attrId = toNumberOrNull(attr.id)
    const selectedByAttribute = selectedValues.find(
      (item) =>
        attrId &&
        item.attributeId === attrId &&
        attr.values.some((value) => Number(value.id) === item.valueId)
    )

    if (selectedByAttribute) {
      attr.selectedValue = attr.values.find((v) => Number(v.id) === selectedByAttribute.valueId) ?? null
      attr.lastQuery = ''
      return
    }

    const selectedByValue = attr.values.find((value) =>
      selectedValueIds.has(Number(value.id))
    )

    attr.selectedValue = selectedByValue ?? null
    attr.lastQuery = ''
  })
}

function fillForm() {
  clearErrors()

  articul.value = props.product?.articul ?? ''
  basePrice.value = props.product?.basePrice ?? 0
  salePrice.value = props.product?.salePrice ?? null
  saleIsActive.value = props.product?.saleIsActive ?? false
  quantity.value = props.product?.quantity ?? 0
  productTypeLastQuery.value = ''
  const matchedType = props.productTypes.find((pt) => pt.id === (props.product?.productTypeId ?? null))
  productTypeSelected.value = matchedType ? { label: matchedType.name, value: matchedType.id } : null

  if (props.product?.images?.length) {
    images.value = props.product.images.map((img) => ({
      id: img.id ?? null,
      url: img.url ?? '',
      storageKey: img.storageKey ?? null,
      isDefault: !!img.isDefault,
      uploading: false
    }))
  } else {
    images.value = [{ id: null, url: '', isDefault: true, uploading: false }]
  }

  if (attributes.value.length) {
    applySelectedAttributeValues()
  }
}

function resetForm() {
  articul.value = ''
  basePrice.value = 0
  salePrice.value = null
  saleIsActive.value = false
  quantity.value = 0
  productTypeSelected.value = null
  productTypeLastQuery.value = ''
  filteredProductTypes.value = []
  images.value = [{ id: null, url: '', isDefault: true, uploading: false }]
  attributes.value = []
  uploadingCount.value = 0
  pendingImageIndex.value = null
  galleryVisible.value = false
  galleryImages.value = []
  clearErrors()
}

function clearErrors() {
  localErrors.value = {
    articul: '',
    productTypeId: '',
    images: ''
  }
}

function validateForm() {
  clearErrors()

  const validImages = images.value
    .map((img) => ({
      id: img.id ?? null,
      url: (img.storageKey || img.url).trim(),
      isDefault: img.isDefault
    }))
    .filter((img) => img.url)

  if (!articul.value.trim()) {
    localErrors.value.articul = 'Артикул обязателен'
  }

  if (!productTypeId.value) {
    if (productTypeLastQuery.value.trim()) {
      localErrors.value.productTypeId = `Основной товар "${productTypeLastQuery.value}" не найден в списке`
    } else {
      localErrors.value.productTypeId = 'Выберите основной товар'
    }
  }

  if (validImages.length === 0 && props.mode === 'edit' && props.product?.isDefault) {
    localErrors.value.images = 'Добавьте хотя бы одно изображение'
  } else if (validImages.length > 0 && validImages.filter((img) => img.isDefault).length !== 1) {
    localErrors.value.images = 'Должно быть ровно одно главное изображение'
  }

  let hasAttrErrors = false
  attributes.value.forEach((attr) => {
    if (attr.lastQuery.trim()) {
      attr.error = `Значение "${attr.lastQuery}" не найдено в списке`
      hasAttrErrors = true
    } else {
      attr.error = ''
    }
  })

  return !Object.values(localErrors.value).some(Boolean) && !hasAttrErrors
}

function addImageFieldByUrl() {
  images.value.push({ id: null, url: '', isDefault: false, uploading: false })
  localErrors.value.images = ''
}

function triggerFileUpload() {
  fileInput.value?.click()
}

async function onFilesSelected(event) {
  const files = Array.from(event.target.files ?? [])
  if (!files.length) return

  for (const file of files) {
    const newImage = { id: null, url: '', isDefault: false, uploading: true }
    images.value.push(newImage)
    const currentIndex = images.value.length - 1

    try {
      await uploadFile(file, currentIndex)
      localErrors.value.images = ''
    } catch (error) {
      if (images.value[currentIndex] && !images.value[currentIndex].url) {
        images.value.splice(currentIndex, 1)
      }
    }
  }

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

async function uploadFileForImage(index) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (index >= 0 && index < images.value.length) {
      images.value[index].uploading = true
      try {
        await uploadFile(file, index)
        localErrors.value.images = ''
      } catch (error) {
        // toast already shown in uploadFile
      } finally {
        if (index >= 0 && index < images.value.length) {
          images.value[index].uploading = false
        }
      }
    }
  }
  input.click()
}

async function uploadFile(file, index) {
  uploadingCount.value++

  try {
    const response = await uploadImage(file, 'products')

    if (index >= 0 && index < images.value.length) {
      images.value[index].url = response.url
      images.value[index].storageKey = response.storageKey
      images.value[index].uploading = false
    }
  } catch (error) {
    toast.error(
      'Не удалось загрузить изображение: ' +
        (error.response?.data?.message || error.message)
    )
    throw error
  } finally {
    uploadingCount.value--
  }
}

async function openGallery() {
  await loadGalleryImages()
  pendingImageIndex.value = null
  galleryVisible.value = true
}

async function openGalleryForImage(index) {
  await loadGalleryImages()
  pendingImageIndex.value = index
  galleryVisible.value = true
}

async function loadGalleryImages() {
  try {
    const loadedImages = await getGalleryImages('products')
    galleryImages.value = loadedImages
  } catch (error) {
    galleryImages.value = []
    toast.error(error.message || 'Ошибка загрузки галереи')
  }
}

function selectFromGallery(url) {
  if (pendingImageIndex.value !== null) {
    if (
      pendingImageIndex.value >= 0 &&
      pendingImageIndex.value < images.value.length
    ) {
      images.value[pendingImageIndex.value].url = url
    }
    pendingImageIndex.value = null
  } else {
    images.value.push({
      id: null,
      url,
      isDefault: false,
      uploading: false
    })
  }

  localErrors.value.images = ''
  galleryVisible.value = false
}

function removeImageField(index) {
  if (images.value.length === 1) return

  images.value.splice(index, 1)

  if (!images.value.some((img) => img.isDefault) && images.value.length > 0) {
    images.value[0].isDefault = true
  }

  localErrors.value.images = ''
}

function setDefaultImage(index) {
  images.value = images.value.map((img, i) => ({
    ...img,
    isDefault: i === index
  }))
  localErrors.value.images = ''
}

function closeDialog() {
  if (props.submitting) return
  emit('close')
}

function submitForm() {
  if (uploadingCount.value > 0) {
    toast.error('Подождите, изображения загружаются')
    return
  }

  if (!validateForm()) return

  const validImages = images.value
    .map((img) => ({
      id: img.id ?? null,
      url: (img.storageKey || img.url).trim(),
      isDefault: img.isDefault
    }))
    .filter((img) => img.url)

  const payload = {
    articul: articul.value.trim(),
    basePrice: Number(basePrice.value ?? 0),
    salePrice:
      salePrice.value === null || salePrice.value === ''
        ? null
        : Number(salePrice.value),
    saleIsActive: saleIsActive.value,
    quantity: Number(quantity.value ?? 0),
    productTypeId: Number(productTypeId.value),
    images: validImages,
    characteristicValues: attributes.value
      .filter((a) => a.selectedValue?.id)
      .map((a) => ({ valueId: Number(a.selectedValue.id) }))
  }

  emit('submit', payload)
}

function filterProductTypes(event) {
  const query = (event.query ?? '').toLowerCase().trim()
  productTypeLastQuery.value = query
  filteredProductTypes.value = query
    ? productTypeOptions.value.filter((pt) => pt.label.toLowerCase().includes(query))
    : [...productTypeOptions.value]
}

function onProductTypeSelect() {
  productTypeLastQuery.value = ''
  localErrors.value.productTypeId = ''
}

function onProductTypeClear() {
  productTypeSelected.value = null
  productTypeLastQuery.value = ''
  localErrors.value.productTypeId = ''
}

function filterAttributeValues(event, attr) {
  const query = (event.query ?? '').toLowerCase().trim()
  attr.lastQuery = query
  attr.filteredValues = query
    ? attr.values.filter((v) => v.value.toLowerCase().includes(query))
    : [...attr.values]
}
function normalizeAttributeValue(value) {
  return (value ?? '').toString().trim().toLowerCase()
}

function attributeValueExists(attr, value) {
  const normalized = normalizeAttributeValue(value)
  if (!normalized) return false

  return attr.values.some((item) => normalizeAttributeValue(item.value) === normalized)
}

function canCreateAttributeValue(attr) {
  return Boolean(attr.lastQuery?.trim()) && !attributeValueExists(attr, attr.lastQuery)
}

async function createAttributeValueFromQuery(attr) {
  const value = attr.lastQuery?.trim()
  if (!value) return

  if (attributeValueExists(attr, value)) {
    attr.error = 'Такое значение уже есть'
    return
  }

  try {
    const created = await createValue(attr.id, value)
    const newValue = {
      id: created.id ?? created.Id ?? created.valueId ?? created.ValueId,
      value: created.value ?? created.Value ?? value
    }

    attr.values.push(newValue)
    attr.filteredValues = [...attr.values]
    attr.selectedValue = newValue
    attr.lastQuery = ''
    attr.error = ''
  } catch (error) {
    attr.error = error.response?.data?.message || error.message || 'Не удалось добавить значение'
  }
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
function isHexColor(str) {
  return HEX_RE.test(str?.trim() ?? '')
}
function isColorAttr(attr) {
  return attr.name?.toLowerCase().includes('цвет')
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(860px, 95vw)' }"
    @update:visible="closeDialog"
  >
    <div class="form-container">
      <div class="field">
        <label>Основной товар</label>
        <AutoComplete
          v-model="productTypeSelected"
          :suggestions="filteredProductTypes"
          optionLabel="label"
          placeholder="Введите основной товар"
          class="autocomplete-wide"
          :disabled="props.mode === 'edit'"
          :loading="loadingAttributes"
          :invalid="Boolean(localErrors.productTypeId)"
          @complete="filterProductTypes"
          @item-select="onProductTypeSelect"
          @clear="onProductTypeClear"
        />
        <small v-if="localErrors.productTypeId" class="field-error">
          {{ localErrors.productTypeId }}
        </small>
      </div>

      <div class="two-columns">
        <div class="field">
          <label>Артикул</label>
          <InputText
            v-model="articul"
            placeholder="Введите артикул"
            class="w-full"
            :invalid="Boolean(localErrors.articul)"
            @input="localErrors.articul = ''"
          />
          <small v-if="localErrors.articul" class="field-error">
            {{ localErrors.articul }}
          </small>
        </div>

        <div class="field">
          <label>Количество</label>
          <InputNumber v-model="quantity" :min="0" class="w-full" />
        </div>
      </div>

      <div class="two-columns">
        <div class="field">
          <label>Базовая цена, €</label>
          <InputNumber
            v-model="basePrice"
            :min="0"
            mode="decimal"
            class="w-full"
          />
        </div>

        <div class="field">
          <label>Цена со скидкой, €</label>
          <InputNumber
            v-model="salePrice"
            :min="0"
            mode="decimal"
            class="w-full"
          />
        </div>
      </div>

      <div class="checkbox-field">
        <Checkbox v-model="saleIsActive" inputId="sale-active" binary />
        <label for="sale-active">Скидка активна</label>
      </div>

      <div v-if="attributes.length" class="attributes-block">
        <div v-for="attr in attributes" :key="attr.id" class="field">
          <label>{{ attr.name }}</label>
          <AutoComplete
            v-model="attr.selectedValue"
            :suggestions="attr.filteredValues"
            optionLabel="value"
            placeholder="Введите или выберите значение"
            class="autocomplete-wide"
            :invalid="Boolean(attr.error)"
            :dropdown="true"
            :forceSelection="false"
            completeOnFocus
            @complete="(e) => filterAttributeValues(e, attr)"
            @item-select="attr.lastQuery = ''; attr.error = ''"
            @clear="attr.lastQuery = ''; attr.error = ''"
          >
            <template #option="{ option }">
              <div class="attr-option">
                <span
                  v-if="isColorAttr(attr) && isHexColor(option.value)"
                  class="color-swatch"
                  :style="{ background: option.value.trim() }"
                />
                <span>{{ option.value }}</span>
              </div>
            </template>
          </AutoComplete>
          <Button
            v-if="canCreateAttributeValue(attr)"
            :label="`Добавить значение: ${attr.lastQuery}`"
            icon="pi pi-plus"
            size="small"
            text
            class="add-attribute-value-btn"
            @click="createAttributeValueFromQuery(attr)"
          />
          <small v-if="attr.error" class="field-error">{{ attr.error }}</small>
        </div>
      </div>

                      <div class="images-block">
        <div class="images-header">
          <label>Изображения</label>
          <div class="images-actions">
            <Button
              label="По URL"
              icon="pi pi-link"
              severity="secondary"
              outlined
              :disabled="props.submitting"
              @click="addImageFieldByUrl"
            />
            <Button
              label="Загрузить файл"
              icon="pi pi-upload"
              severity="secondary"
              outlined
              :disabled="props.submitting"
              @click="triggerFileUpload"
            />
            <Button
              label="Из галереи"
              icon="pi pi-images"
              severity="secondary"
              outlined
              :disabled="props.submitting"
              @click="openGallery"
            />
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          multiple
          style="display: none"
          @change="onFilesSelected"
        />

        <div
          v-for="(image, index) in images"
          :key="image.id ?? index"
          class="image-row"
          :class="{ 'image-uploading': image.uploading }"
        >
          <div class="image-url-input">
            <InputText
              v-model="image.url"
              placeholder="Вставьте URL изображения"
              class="image-input"
              :disabled="image.uploading || props.submitting"
              @input="localErrors.images = ''"
            />
            <Button
              v-if="!image.url"
              icon="pi pi-upload"
              severity="secondary"
              outlined
              :loading="image.uploading"
              :disabled="props.submitting"
              title="Загрузить файл"
              @click="uploadFileForImage(index)"
            />
            <Button
              v-if="!image.url"
              icon="pi pi-images"
              severity="secondary"
              outlined
              :disabled="props.submitting || image.uploading"
              title="Выбрать из галереи"
              @click="openGalleryForImage(index)"
            />
          </div>

          <div class="image-actions">
            <Button
              :label="image.isDefault ? 'Главное' : 'Сделать главным'"
              :severity="image.isDefault ? 'success' : 'secondary'"
              outlined
              size="small"
              :disabled="props.submitting || image.uploading"
              @click="setDefaultImage(index)"
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              outlined
              size="small"
              :disabled="props.submitting || image.uploading"
              @click="removeImageField(index)"
            />
          </div>
        </div>

        <div v-if="uploadingCount > 0" class="uploading-status">
          <i class="pi pi-spin pi-spinner"></i>
          Загрузка {{ uploadingCount }} изображений...
        </div>

        <small v-if="localErrors.images" class="field-error">
          {{ localErrors.images }}
        </small>
      </div>
    </div>

    <Dialog
      v-model:visible="galleryVisible"
      header="Галерея изображений"
      :style="{ width: 'min(800px, 95vw)' }"
      modal
    >
      <div class="gallery-grid">
        <div
          v-for="img in galleryImages"
          :key="img.url"
          class="gallery-item"
          @click="selectFromGallery(img.url)"
        >
          <img :src="img.url" :alt="img.name" />
          <div class="gallery-item-name">{{ img.name }}</div>
        </div>
        <div v-if="galleryImages.length === 0" class="gallery-empty">
          Нет загруженных изображений
        </div>
      </div>

      <template #footer>
        <Button
          label="Закрыть"
          severity="secondary"
          @click="galleryVisible = false"
        />
      </template>
    </Dialog>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="props.submitting"
        @click="closeDialog"
      />
      <Button
        :label="submitLabel"
        :icon="props.mode === 'edit' ? 'pi pi-check' : 'pi pi-plus'"
        :loading="props.submitting"
        :disabled="props.submitting || uploadingCount > 0"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.images-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.images-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.image-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fafafa;
  transition: opacity 0.3s;
}

.image-uploading {
  opacity: 0.7;
}

.image-url-input {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.image-input {
  flex: 1;
  min-width: 200px;
}

.attributes-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.autocomplete-wide {
  width: min(480px, 100%);
}

.attr-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
}

.image-actions {
  display: flex;
  gap: 8px;
}

.uploading-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #e3f2fd;
  border-radius: 6px;
  font-size: 13px;
  color: #1976d2;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
}

.gallery-item {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  text-align: center;
}

.gallery-item:hover {
  border-color: #c41217;
  transform: scale(1.02);
}

.gallery-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.gallery-item-name {
  padding: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.gallery-empty {
  text-align: center;
  padding: 40px;
  color: #999;
}

@media (max-width: 700px) {
  .two-columns {
    grid-template-columns: 1fr;
  }

  .image-actions {
    flex-direction: column;
  }

  .images-actions {
    flex-direction: column;
    width: 100%;
  }

  .image-url-input {
    flex-direction: column;
  }

  .image-input {
    width: 100%;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}
</style>

